import React from "react";

import { useState } from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { Upload, User, Briefcase, MessageSquare } from "lucide-react";

import { Button } from "../../../shadcn/components/ui/Button";
import { Input } from "../../../shadcn/components/ui/Input";
import { Textarea } from "../../../shadcn/components/ui/Textarea";
import { Label } from "../../../shadcn/components/ui/Label";

export default function ReviewsPanel() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    // Simulating API call
    await new Promise((resolve) => setTimeout(resolve, 2000));
    console.log(data);
    setIsSubmitting(false);
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className=" p-4 md:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-4xl mx-auto bg-white rounded-lg overflow-hidden"
      >
        <div className="bg-purple-600 p-6 md:p-8">
          <h1 className="text-2xl md:text-3xl font-bold text-white">
            Add New Review
          </h1>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="p-6 md:p-8 space-y-6"
        >
          {/* Image Upload Section */}
          <div className="space-y-2">
            <Label htmlFor="reviewerImage">Reviewer Image</Label>
            <div className="flex flex-col items-center space-y-4">
              {previewImage ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="relative group"
                >
                  <img
                    src={previewImage || "/placeholder.svg"}
                    alt="Preview"
                    className="w-32 h-32 rounded-full object-cover border-4 border-purple-200"
                  />
                  <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Label
                      htmlFor="reviewerImage"
                      className="text-white cursor-pointer text-sm"
                    >
                      Change Image
                    </Label>
                  </div>
                </motion.div>
              ) : (
                <div className="w-32 h-32">
                  <label
                    htmlFor="reviewerImage"
                    className="flex flex-col items-center justify-center w-full h-full rounded-full border-4 border-purple-200 border-dashed cursor-pointer bg-purple-50 hover:bg-purple-100 transition-colors duration-300"
                  >
                    <Upload className="w-8 h-8 text-purple-400" />
                    <span className="text-sm text-purple-500 mt-2">Upload</span>
                  </label>
                </div>
              )}
              <Input
                id="reviewerImage"
                type="file"
                {...register("reviewerImage", {
                  required: "Reviewer image is required",
                })}
                className="hidden"
                accept="image/*"
                onChange={handleImageChange}
              />
            </div>
            {errors.reviewerImage && (
              <p className="text-red-500 text-sm text-center">
                {errors.reviewerImage.message}
              </p>
            )}
          </div>

          {/* Reviewer Details Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  id="name"
                  {...register("name", { required: "Name is required" })}
                  className="w-full pl-10"
                  placeholder="         Enter reviewer name"
                />
              </div>
              {errors.name && (
                <p className="text-red-500 text-sm">{errors.name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="profession">Profession</Label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  id="profession"
                  {...register("profession", {
                    required: "Profession is required",
                  })}
                  className="w-full pl-10 space-x-10"
                  placeholder="        Enter profession"
                />
              </div>
              {errors.profession && (
                <p className="text-red-500 text-sm">
                  {errors.profession.message}
                </p>
              )}
            </div>
          </div>

          {/* Review Text Section */}
          <div className="space-y-2">
            <Label htmlFor="review">Review</Label>
            <div className="relative">
              <MessageSquare className="absolute left-3 top-3 text-gray-400" />
              <Textarea
                id="review"
                {...register("review", {
                  required: "Review text is required",
                })}
                className="w-full pl-10 min-h-[120px]"
                placeholder="Enter the review text"
              />
            </div>
            {errors.review && (
              <p className="text-red-500 text-sm">{errors.review.message}</p>
            )}
          </div>

          {/* Submit Button */}
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              type="submit"
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 rounded-lg transition-colors duration-300"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <span className="animate-spin mr-2">⏳</span>
                  Adding Review...
                </span>
              ) : (
                "Add Review"
              )}
            </Button>
          </motion.div>
        </form>
      </motion.div>
    </div>
  );
}
